import React from "react"
import CircularProgress from "./CircularProgress"
import { AvatarGroup } from "../DashboardComponents/AvatarGroup"
import CustomButton from "../OnboardingComponents/CustomButton"
import { ICONS } from "../../constants/icons"
import { getDaysLeft } from "../../common/data/utility"
import { getUserAvatarUrl } from "../../helpers/userAvatar"

/**
 * ProjectHeaderMeta — status-specific header actions (Current / Past / Future).
 * Current (in-progress) matches Figma: compact green progress + ring + avatars + CTA.
 */
const ProjectHeaderMeta = ({ project, onAddCollaborator }) => {
  const totalCollaborators = project?.members?.length || 0
  const canAddCollaborator =
    Boolean(onAddCollaborator) &&
    Boolean(project?.is_current_user_active_member)

  const avatars = (
    <AvatarGroup
      people={project?.members?.map(c => ({
        name:
          c.user?.full_name ||
          `${c.user?.first_name || ""} ${c.user?.last_name || ""}`.trim() ||
          c.user?.name ||
          c.name,
        src: getUserAvatarUrl(c.user) || c.avatar,
      }))}
      size="sm"
      max={3}
      total={totalCollaborators}
      className="project-detail__collab-avatars"
    />
  )

  const addCollaborator = canAddCollaborator ? (
    <CustomButton
      variant="primary"
      trailingIconSrc={ICONS.plus}
      trailingIconAlt=""
      className="project-detail__add-collaborator"
      onClick={onAddCollaborator}
    >
      Add Collaborator
    </CustomButton>
  ) : null

  if (project?.status === "upcoming" || project?.status?.status === "upcoming") {
    return (
      <>
        <div className="project-detail__starting-from">
          <img src={ICONS.session} alt="" />
          <span>
            Starting from <b>{project?.startingFrom}</b>
          </span>
        </div>
        {avatars}
        {addCollaborator}
      </>
    )
  }

  const progress = Number(project?.progress)
  const daysLeftRaw =
    project?.daysLeft != null && project?.daysLeft !== ""
      ? Number(project.daysLeft)
      : getDaysLeft(project?.end_date)
  const daysLeft = Number.isFinite(daysLeftRaw) ? daysLeftRaw : null
  const isOngoing = Boolean(project?.isOngoing || project?.is_ongoing)
  const isOverdue = Boolean(project?.isOverdue || project?.is_overdue)
  const hasProgress = Number.isFinite(progress)
  const ringValue = hasProgress ? Math.max(0, Math.min(100, progress)) : daysLeft ?? 0
  const ringMax = hasProgress ? 100 : Math.max(daysLeft ?? 1, 1)

  if (project?.status?.status === "in-progress" && (daysLeft != null || hasProgress)) {
    return (
      <>
        <div className="project-detail__progress project-detail__progress--circular">
          <div className="project-detail__progress-copy">
            {hasProgress ? (
              <span className="project-detail__progress-label">
                {progress % 1 === 0 ? progress : progress.toFixed(2)}% Completed
              </span>
            ) : null}
            {isOverdue ? (
              <span className="project-detail__progress-note">Overdue</span>
            ) : isOngoing ? null : daysLeft != null ? (
              <span className="project-detail__progress-note">
                {daysLeft} {daysLeft === 1 ? "day" : "days"} left
              </span>
            ) : null}
          </div>
          <CircularProgress
            value={ringValue}
            max={ringMax}
            tone="green"
            size={24}
            strokeWidth={3}
            className="project-detail__progress-ring"
          />
        </div>
        {avatars}
        {addCollaborator}
      </>
    )
  }

  return (
    <>
      {avatars}
      {addCollaborator}
    </>
  )
}

export default ProjectHeaderMeta
